/**
 * Cosmetic seed derivation (PRD §3.2, §7.5).
 *
 * Cosmetic appearance is deterministic from `(matchSeed, side, agentName,
 * archetype)`. Two viewers of the same replay must produce visually
 * identical mech compositions — chunk-direction and debris physics
 * resting positions are exempted, but everything that participates in
 * the seeded composition function is byte-equivalent.
 *
 * SplitMix64 keeps parity with the engine's PRNG primitive; we use 32-
 * bit truncated outputs so the composer can index `Math.random`-style
 * tables.
 */

/** FNV-1a 32-bit, used for stable string mixing into the seed. */
function fnv1a(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** SplitMix64-style mix on a 32-bit input, returns 32-bit unsigned. */
function mix32(x: number): number {
  let z = (x + 0x9e3779b9) >>> 0;
  z = Math.imul(z ^ (z >>> 16), 0x85ebca6b) >>> 0;
  z = Math.imul(z ^ (z >>> 13), 0xc2b2ae35) >>> 0;
  return (z ^ (z >>> 16)) >>> 0;
}

export interface CosmeticSeedInput {
  matchSeed: number | string;
  side: "A" | "B";
  agentName: string;
  archetype?: string;
}

/**
 * Combine all inputs into a single 32-bit seed value. Stable across
 * processes; only depends on the input strings + the constants in
 * this file.
 */
export function deriveCosmeticSeed(input: CosmeticSeedInput): number {
  const seedNum =
    typeof input.matchSeed === "number"
      ? input.matchSeed >>> 0
      : fnv1a(input.matchSeed);
  let acc = mix32(seedNum);
  acc = mix32(acc ^ (input.side === "A" ? 0xa0a0a0a0 : 0xb0b0b0b0));
  acc = mix32(acc ^ fnv1a(input.agentName));
  if (input.archetype) acc = mix32(acc ^ fnv1a(input.archetype));
  return acc;
}

/**
 * Stateful seeded RNG built from a 32-bit seed. `next()` returns a
 * uniform float in [0, 1); `nextInt(n)` is `[0, n)`; `pick(arr)`
 * picks deterministically from a non-empty array.
 *
 * Identical seeds produce identical sequences. The composer uses this
 * to walk through part-library tables once, in a fixed order.
 */
export class SeededRng {
  private s: number;

  constructor(seed: number) {
    this.s = seed >>> 0;
  }

  next(): number {
    this.s = mix32(this.s + 1);
    // Map to [0,1) using 32-bit precision.
    return this.s / 0x1_0000_0000;
  }

  nextInt(n: number): number {
    if (n <= 0) throw new Error("nextInt requires n > 0");
    return Math.floor(this.next() * n);
  }

  pick<T>(arr: ReadonlyArray<T>): T {
    if (arr.length === 0) throw new Error("pick from empty array");
    return arr[this.nextInt(arr.length)];
  }
}
